import { createContext, useContext } from "react";
import { createPortal } from "react-dom";

/**
 * The screen's own heading, rendered into the frame's header bar rather than
 * into a strip of its own.
 *
 * The queue used to stack three full-width bars above the first ticket: the
 * platform's account row, holding one avatar; the view's name and count; and
 * the filters. Roughly a hundred pixels of chrome to say "All open tickets, 69,
 * unfiltered" -- on the screen whose entire job is to show as many rows as it
 * can. They are one row now, and the three of them fit in it with space to
 * spare because none of them was ever wide.
 *
 * A portal rather than a prop threaded down through the shell. What the header
 * says belongs to the screen -- the view's name, its count, its filters, and in
 * the Helpdesk's case controls that read the open ticket's position in the list
 * -- and all of that is state the page owns and the frame has no business
 * holding. The portal keeps the markup where the state is and moves only the
 * pixels.
 *
 * A page that renders no `PageHeader` gets what it had before: the bar with the
 * account menu alone at the right of it.
 */
const PageHeaderSlot = createContext<HTMLElement | null>(null);

export const PageHeaderSlotProvider = PageHeaderSlot.Provider;

export function PageHeader({ children }: { children: React.ReactNode }) {
  const slot = useContext(PageHeaderSlot);

  /*
   * Nothing outside the frame, rather than a fallback bar. A second heading
   * drawn somewhere unexpected is harder to diagnose than a missing one, and
   * every screen in this bundle renders inside `AppFrame`.
   */
  return slot ? createPortal(children, slot) : null;
}
